import type { MenuDataItem } from '@ant-design/pro-components';
import * as icons from '@ant-design/icons';
import React from 'react';
import { getApiMenu, MenuItem } from '@/services/obas-api/menu';

let asyncMenuData: MenuItem[] = [];

/** getInitialState 与 render 共用同一 Promise，并发或先后调用也只发一次 /api/menu */
let menuLoadPromise: Promise<MenuItem[]> | null = null;

let menuRevision = 0;
const menuRevisionListeners = new Set<() => void>();

function bumpMenuRevision(): void {
  menuRevision += 1;
  menuRevisionListeners.forEach((cb) => cb());
}

export function getMenuRevision(): number {
  return menuRevision;
}

/** 菜单缓存更新后通知 ProLayout 重新执行 menuDataRender */
export function subscribeMenuRevision(listener: () => void): () => void {
  menuRevisionListeners.add(listener);
  return () => {
    menuRevisionListeners.delete(listener);
  };
}

export function getAsyncMenuData(): MenuItem[] {
  return asyncMenuData;
}

export async function loadAppMenuOnce(): Promise<MenuItem[]> {
  if (menuLoadPromise) return menuLoadPromise;
  menuLoadPromise = (async () => {
    try {
      const res: any = await getApiMenu({});
      const raw = res?.data;
      let menu: MenuItem[] = [];
      if (Array.isArray(raw)) menu = raw;
      else if (raw && typeof raw === 'object') menu = [raw as MenuItem];
      asyncMenuData = menu;
      return menu;
    } catch {
      menuLoadPromise = null;
      return asyncMenuData;
    }
  })();
  return menuLoadPromise;
}

/** 丢弃缓存并重新请求 GET /api/menu（不可从 app.tsx 导出，Umi 会误注册为插件） */
export async function refreshAppMenu(): Promise<MenuItem[]> {
  menuLoadPromise = null;
  const menu = await loadAppMenuOnce();
  bumpMenuRevision();
  return menu;
}

/** 与 app.tsx patchClientRoutes 内 normalizePath 保持一致，便于路由与接口 path 对齐 */
function normalizePath(p: string | undefined): string {
  if (!p) return '';
  let s = p.startsWith('/') ? p : `/${p}`;
  s = s.split(/[?#]/)[0];
  s = s.replace(/\/$/, '');
  s = s.replace(/\/index$/, '');
  s = s.replace(/\/+/g, '/');
  return s;
}

function findMenuItemByPath(
  items: MenuItem[] | undefined,
  path: string,
): MenuItem | undefined {
  if (!items || !path) return undefined;
  const norm = normalizePath(path);
  for (const it of items) {
    const itPath = normalizePath(it.path);
    if (itPath === norm) return it;
    const found = findMenuItemByPath(it.children, path);
    if (found) return found;
  }
  return undefined;
}

function flattenMenuItems(items: MenuItem[] | undefined) {
  const byRoute = new Map<string, MenuItem>();
  const byPath = new Map<string, MenuItem>();
  if (!items || items.length === 0)
    return { byRoute, byPath, roots: items || [] };
  const stack = [...items];
  while (stack.length) {
    const it = stack.pop()!;
    const menuRoute = (it as any).route || it.path;
    const r = normalizePath(menuRoute);
    const p = normalizePath(it.path);
    if (r && !byRoute.has(r)) byRoute.set(r, it);
    if (p && !byPath.has(p)) byPath.set(p, it);
    if (it.children && it.children.length) stack.push(...it.children);
  }
  return { byRoute, byPath, roots: items };
}

function resolveIconFromMenu(menuMatch: MenuItem): React.ReactNode | undefined {
  const iconFromMenu = (menuMatch as any).icon;
  if (iconFromMenu === undefined || iconFromMenu === null) return undefined;
  if (typeof iconFromMenu === 'string') {
    if (iconFromMenu === '') return undefined;
    const IconComp = (icons as any)[iconFromMenu];
    if (IconComp) return React.createElement(IconComp);
    return iconFromMenu;
  }
  return iconFromMenu as React.ReactNode;
}

function collectMenuPathDisplayOrder(
  items: MenuItem[] | undefined,
  out: Map<string, number> = new Map(),
): Map<string, number> {
  if (!items) return out;
  for (const it of items) {
    const rawPath = it.path || (it as any).route;
    const key = normalizePath(typeof rawPath === 'string' ? rawPath : '');
    if (key && typeof (it as any).displayOrder === 'number') {
      out.set(key, (it as any).displayOrder);
    }
    if (it.children?.length) collectMenuPathDisplayOrder(it.children, out);
  }
  return out;
}

function sortMenuDataByDisplayOrder(
  nodes: MenuDataItem[],
  orderMap: Map<string, number>,
): MenuDataItem[] {
  const sorted = [...nodes]
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const pa = normalizePath(a.item.path as string);
      const pb = normalizePath(b.item.path as string);
      const ra = normalizePath((a.item as any).route);
      const rb = normalizePath((b.item as any).route);
      const oa = orderMap.get(pa) ?? orderMap.get(ra) ?? 1_000_000 + a.idx;
      const ob = orderMap.get(pb) ?? orderMap.get(rb) ?? 1_000_000 + b.idx;
      return oa - ob;
    })
    .map(({ item }) => item);
  return sorted.map((node) => {
    const rawChildren = (node as any).children || (node as any).routes;
    if (!rawChildren?.length) return node;
    const sortedKids = sortMenuDataByDisplayOrder(rawChildren, orderMap);
    return {
      ...node,
      children: sortedKids,
      routes: sortedKids,
    } as unknown as MenuDataItem;
  });
}

function mergeApiFieldsOntoLayoutMenu(
  menuData: MenuDataItem[],
  flat: ReturnType<typeof flattenMenuItems>,
): MenuDataItem[] {
  return menuData.map((node) => {
    const rawChildren = (node as any).children || (node as any).routes;
    const mergedChildren = rawChildren?.length
      ? mergeApiFieldsOntoLayoutMenu(rawChildren, flat)
      : undefined;

    const routeKey = normalizePath((node as any).route || node.path);
    let menuMatch =
      flat.byRoute.get(routeKey) ||
      flat.byPath.get(routeKey) ||
      findMenuItemByPath(flat.roots, node.path as string);

    let updated: MenuDataItem = { ...node };
    if (mergedChildren?.length) {
      updated = {
        ...updated,
        children: mergedChildren,
        routes: mergedChildren,
      } as unknown as MenuDataItem;
    }

    if (menuMatch?.name) {
      (updated as any).name = menuMatch.name;
    }

    if (menuMatch) {
      const resolved = resolveIconFromMenu(menuMatch);
      if (resolved !== undefined) {
        updated.icon = resolved as any;
      }
    }

    return updated;
  });
}

/** fallback 路径：从已合并的树里去掉接口标记为禁用的菜单 */
function filterDisabledLayoutNodesByApi(
  nodes: MenuDataItem[],
  flat: ReturnType<typeof flattenMenuItems>,
): MenuDataItem[] {
  return nodes
    .map((node) => {
      const routeKey = normalizePath((node as any).route || node.path);
      const menuMatch =
        flat.byRoute.get(routeKey) ||
        flat.byPath.get(routeKey) ||
        findMenuItemByPath(flat.roots, node.path as string);
      if (menuMatch && (menuMatch as any).isEnabled === false) {
        return null;
      }
      const rawChildren = (node as any).children || (node as any).routes;
      if (!rawChildren?.length) return node;
      const filteredKids = filterDisabledLayoutNodesByApi(rawChildren, flat);
      if (!filteredKids.length) {
        const next = { ...node } as MenuDataItem;
        delete (next as any).children;
        delete (next as any).routes;
        return next;
      }
      return {
        ...node,
        children: filteredKids,
        routes: filteredKids,
      } as unknown as MenuDataItem;
    })
    .filter((n): n is MenuDataItem => n !== null);
}

/** 将当前 ProLayout 菜单树按 path/route 扁平化，用于与接口树对齐（移动父级后按接口重组子节点） */
function flattenLayoutMenuByPath(
  nodes: MenuDataItem[] | undefined,
  map: Map<string, MenuDataItem> = new Map(),
): Map<string, MenuDataItem> {
  if (!nodes?.length) return map;
  for (const node of nodes) {
    const p = normalizePath(node.path as string);
    const r = normalizePath((node as any).route);
    if (p && !map.has(p)) map.set(p, node);
    if (r && !map.has(r)) map.set(r, node);
    const kids = (node as any).children || (node as any).routes;
    if (kids?.length) flattenLayoutMenuByPath(kids, map);
  }
  return map;
}

function sortApiMenuNodes(items: MenuItem[] | undefined): MenuItem[] {
  if (!items?.length) return [];
  return [...items].sort(
    (a, b) => ((a as any).displayOrder ?? 0) - ((b as any).displayOrder ?? 0),
  );
}

/** 接口未返回 isEnabled 或 true 视为启用；仅明确 false 为禁用 */
function isApiMenuItemEnabled(apiItem: MenuItem): boolean {
  return (apiItem as any).isEnabled !== false;
}

/** 取 routes.tsx 等静态路由上挂在父节点下的子节点（用于与接口子项合并） */
function getStaticChildRoutes(base: MenuDataItem): MenuDataItem[] {
  const raw = (base as any).routes || (base as any).children;
  return Array.isArray(raw) ? (raw as MenuDataItem[]) : [];
}

/**
 * 接口子树未覆盖的静态子路由（如 hideInMenu 的「历史版本」页）仍挂回父节点，
 * 否则顶栏按接口重建后会删掉 routes，面包屑无法匹配到第三级。
 */
function mergeApiBuiltChildrenWithStaticOnlyRoutes(
  builtFromApi: MenuDataItem[],
  base: MenuDataItem,
  flat: ReturnType<typeof flattenMenuItems>,
): MenuDataItem[] {
  const staticKids = getStaticChildRoutes(base);
  if (!staticKids.length) return builtFromApi;

  const pathsFromApi = new Set(
    builtFromApi
      .map((n) =>
        normalizePath(
          ((n as any).path || (n as any).route || '') as string,
        ),
      )
      .filter(Boolean),
  );

  const extras = staticKids.filter((n) => {
    const p = normalizePath(
      ((n as any).path || (n as any).route || '') as string,
    );
    if (!p || pathsFromApi.has(p)) return false;
    const menuMatch =
      flat.byRoute.get(p) ||
      flat.byPath.get(p) ||
      findMenuItemByPath(flat.roots, p);
    // 接口已配置且明确禁用时，不要用 routes.tsx 静态项补回顶栏
    if (menuMatch && !isApiMenuItemEnabled(menuMatch)) return false;
    return true;
  });

  if (!extras.length) return builtFromApi;
  return [...builtFromApi, ...extras];
}

/**
 * 按接口返回的树形结构重组顶栏菜单：子项挂到接口中的父级下（与 routes.tsx 静态层级解耦）。
 * 禁用的菜单项（isEnabled === false）不展示在顶栏。
 */
function buildLayoutMenuFromApiTree(
  apiNodes: MenuItem[] | undefined,
  pathMap: Map<string, MenuDataItem>,
  flat: ReturnType<typeof flattenMenuItems>,
): MenuDataItem[] {
  if (!apiNodes?.length) return [];
  const out: MenuDataItem[] = [];
  for (const apiItem of sortApiMenuNodes(apiNodes)) {
    if (!isApiMenuItemEnabled(apiItem)) continue;

    const key = normalizePath(
      apiItem.path || (apiItem as any).route || '',
    );
    if (!key) continue;
    const base = pathMap.get(key);
    if (!base) continue;

    const rawApiChildren =
      apiItem.children?.filter((c: MenuItem | null | undefined) => !!c) || [];
    const builtChildren =
      rawApiChildren.length > 0
        ? buildLayoutMenuFromApiTree(rawApiChildren, pathMap, flat)
        : [];

    const node: MenuDataItem = { ...base };
    delete (node as any).children;
    delete (node as any).routes;

    if (apiItem.name) (node as any).name = apiItem.name;
    const resolved = resolveIconFromMenu(apiItem);
    if (resolved !== undefined) (node as any).icon = resolved;

    if (builtChildren.length > 0) {
      const merged = mergeApiBuiltChildrenWithStaticOnlyRoutes(
        builtChildren,
        base,
        flat,
      );
      (node as any).children = merged;
      (node as any).routes = merged;
    } else {
      const staticOnly = getStaticChildRoutes(base);
      if (staticOnly.length) {
        (node as any).children = staticOnly;
        (node as any).routes = staticOnly;
      }
    }

    out.push(node);
  }
  return out;
}

/**
 * 用 GET /api/menu 的最新数据更新顶栏菜单：父子层级、名称、图标、顺序。
 * （patchClientRoutes 只在启动时写一次路由；层级与排序需按接口树重建。）
 */
export function applyApiMenuToLayoutMenu(menuData: MenuDataItem[]): MenuDataItem[] {
  if (!asyncMenuData.length) return menuData;
  try {
    const pathMap = flattenLayoutMenuByPath(menuData);
    const flat = flattenMenuItems(asyncMenuData);
    const rebuilt = buildLayoutMenuFromApiTree(asyncMenuData, pathMap, flat);
    if (rebuilt.length > 0) {
      return rebuilt;
    }
    const merged = mergeApiFieldsOntoLayoutMenu(menuData, flat);
    const withoutDisabled = filterDisabledLayoutNodesByApi(merged, flat);
    const orderMap = collectMenuPathDisplayOrder(asyncMenuData);
    return sortMenuDataByDisplayOrder(withoutDisabled, orderMap);
  } catch {
    return menuData;
  }
}
